<template>
  <div class="main-content">
    <div class="section__content section__content--p30">
      <div class="container-fluid create-profile-container p-b-40">
        <v-card class="p-b-30 p-t-30 p-l-20">
          <v-form v-model="isFormValid">
            <div class="el-loading-mask" v-if="loading">
              <div class="el-loading-spinner">
                <svg viewBox="25 25 50 50" class="circular">
                  <circle cx="50" cy="50" r="20" fill="none" class="path"></circle>
                </svg>
              </div>
            </div>
            <!-- Card Title -->
            <div class="row">
              <div class="btn-actions-pane-left">
                <h3 class="title-1 p-l-145">
                  <v-btn
                    text icon
                    class="m-0"
                    @click="$router.back()">
                    <i class="zmdi zmdi-long-arrow-left"></i>
                  </v-btn>
                  <span v-if="!isEditing" style="padding-left: 2px;">
                    {{ text('new_profile_title') }}
                  </span>
                  <span v-else style="padding-left: 2px;">{{ text('edit_encoding_profile_header') }} <span class="p-l-20 status--gray"> ID={{ form.id }}</span></span>
                </h3>
              </div>
              <div class="btn-actions-pane-right p-r-45"></div>
            </div>
            <!-- Name and Profile -->
            <div class="section">
              <div class="row p-b-10">
                <div class="col-md-2 col-l"></div>
                <div class="col-md-10 section-description">
                  {{ text('profile_name_text') }}
                </div>
              </div>
              <div class="row">
                <div class="col-md-2 col-l"></div>
                <div class="col-md-10 p-r-0">
                  <v-text-field
                    v-model="form.name"
                    id="name"
                    :label="text('profile_name_label')"
                    :counter="255"
                    :rules="rules.nameRules"
                    hint="e.g., AAC 96 - H264: 288/360/408/720/1080/4K"
                    required></v-text-field>
                </div>
              </div>
            </div>
            <!-- AUDIO -->
            <div class="section">
              <div class="row p-b-10">
                <div class="col-md-2 col-l">
                  <h2>{{ text('audio') }}</h2>
                </div>
                <div class="col-md-10 section-description">
                  {{ text('profile_audio_text') }}
                </div>
              </div>
              <div class="row p-b-10">
                <div class="col-md-2 col-l"></div>
                <div class="col-md-10 p-r-0 codec">
                  <v-select
                    hide-details
                    outline
                    v-model="form.audio.codec"
                    item-text="title"
                    item-value="extId"
                    :label="text('codec_label')"
                    :items="audioCodecs">
                  </v-select>
                </div>
              </div>
              <div class="row">
                <div class="col-md-2 col-l"></div>
                <div class="col-md-5 p-r-0" style="max-width: 300px;">
                  <v-select
                    v-model="form.audio.sampleRate"
                    :label="text('sample_rate_label')"
                    :items="sampleRates"
                    :suffix="sampleRateSuffix">
                  </v-select>
                </div>
                <div class="col-md-5 p-r-0" style="max-width: 333px;">
                  <v-select
                    v-model="form.audio.channels"
                    :label="text('channel_count_label')"
                    item-text="title"
                    item-value="value"
                    :items="audioChannels">
                    <template v-slot:append-outer>
                      <audio-channels-tooltip/>
                    </template>
                  </v-select>
                </div>
              </div>
              <div class="row">
                <div class="col-md-2 col-l"></div>
                <div class="col-md-5 p-r-0" style="max-width: 300px;">
                  <v-select
                    v-model="form.audio.bitrate"
                    :label="text('bitrate_label')"
                    :items="audioBitrates"
                    :suffix="audioBitrateSuffix">
                  </v-select>
                </div>
                <div class="col-md-5 p-r-0"
                     style="max-width: 300px;"
                     v-if="form.audio.bitrate === 'Custom'">
                  <v-text-field
                    :label="text('custom_bitrate_label')"
                    v-model="form.audio.customBitrate"
                    suffix="Kbps"
                    :rules="rules.customAudioBitrateRules">
                  </v-text-field>
                </div>
              </div>
            </div>
            <!-- VIDEO -->
            <div class="section">
              <div class="row p-b-10">
                <div class="col-md-2 col-l">
                  <h2>{{ text('video') }}</h2>
                </div>
                <div class="col-md-10 section-description">
                  {{ text('profile_video_text') }}
                </div>
              </div>
              <div class="row p-b-10">
                <div class="col-md-2 col-l"></div>
                <div class="col-md-10 p-r-0 codec">
                  <v-select
                    hide-details
                    outline
                    v-model="form.video.codec"
                    item-text="title"
                    item-value="extId"
                    :label="text('codec_label')"
                    :items="videoCodecs">
                  </v-select>
                </div>
              </div>
              <div class="row">
                <div class="col-md-2 col-l"></div>
                <div class="col-md-10 p-r-0" style="max-width: 300px;">
                  <v-select
                    hide-details
                    v-if="form.video.codec === 'h264'"
                    v-model="form.video.h264encoder"
                    :label="text('encoder_label')"
                    :items="h264Encoders">
                  </v-select>
                  <v-select
                    hide-details
                    v-if="form.video.codec === 'h265'"
                    v-model="form.video.h265encoder"
                    :label="text('encoder_label')"
                    :items="h265Encoders">
                  </v-select>
                </div>
              </div>
              <div class="row p-t-30">
                <div class="col-md-2 col-l"></div>
                <div class="col-md-10 p-r-0">
                  <table class="profile-streams">
                    <thead>
                    <tr>
                      <th>{{ text('stream') }}</th>
                      <th v-if="form.video.codec.startsWith('h26')">
                        {{ text('profile') }} / {{ text('level') }}
                      </th>
                      <th>{{ text('resolution') }}</th>
                      <th>{{ text('bitrate') }}</th>
                      <th>{{ text('resize_mode') }}</th>
                      <th>{{ text('frame_rate') }}</th>
                      <th>
                        KeyInt
                        <span v-if="currentProject === 'file-transcoding'">/ MinKeyInt</span>
                      </th>
                      <th v-if="form.video.codec.startsWith('h26')">
                        {{ text('ref_frames') }} / BFrames
                      </th>
                      <th></th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(stream, i) of form.video.streams[form.video.codec]" :key="i">
                      <td># {{ i+1 }}</td>
                      <td v-if="form.video.codec === 'h264'">
                        {{ stream.h264CodecProfile }} / {{ stream.h264CodecLevel }}
                      </td>
                      <td v-if="form.video.codec === 'h265'">
                        {{ stream.h265CodecProfile }} / {{ stream.h265CodecLevel }}
                      </td>
                      <td>{{ stream.resolution }}</td>
                      <td>{{ stream.bitrate }}</td>
                      <td>{{ stream.resizeMode }}</td>
                      <td>{{ stream.frameRate }}</td>
                      <td>
                        {{ stream.keyframeInt || '0' }}
                        <span v-if="currentProject === 'file-transcoding'">
                          / {{ stream.minKeyframeInt || '0' }}
                        </span>
                      </td>
                      <td v-if="form.video.codec.startsWith('h26')">
                        {{ stream.h264ReferenceFrames || '0' }} / {{ stream.h264BFrames || '0' }}
                      </td>
                      <td class="p-0">
                        <v-btn text icon class="p-0 m-0"
                               @click.stop="editStream(i, stream)">
                          <i class="zmdi zmdi-edit"></i>
                        </v-btn>
                        <v-btn text icon class="p-0 m-0"
                               @click.stop="openStreamRemoveDialog(i)">
                          <i class="zmdi zmdi-delete"></i>
                        </v-btn>
                      </td>
                    </tr>
                    <tr>
                      <td :colspan="form.video.codec.startsWith('h26') ? '9' : '7'"
                          class="text-center" style="padding: 30px !important;">
                        <span class="p-r-10" v-if="!form.video.streams[form.video.codec].length">
                          {{ text('at_least_one_stream') }}
                        </span>
                        <v-btn
                          outline small
                          class="m-0"
                          @click="addNewStream()">
                          {{ text('new_video_stream_btn') }}
                        </v-btn>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <!-- Add Stream Dialog -->
            <v-dialog v-model="streamDialog" persistent max-width="620px">
              <v-card class="p-t-10 p-r-20 p-l-20 p-b-10">
                <v-card-title>
                  <span class="headline" v-if="!streamEditNumber">{{ text('new_video_stream_label') }}</span>
                  <span class="headline" v-else>{{ text('edit_video_stream_label') }} #{{ streamEditNumber }}</span>
                </v-card-title>
                <v-card-text>
                  <div class="row">
                    <!-- H.264 Profile and Level -->
                    <div class="col-md-12" v-if="form.video.codec === 'h264'">
                      <div class="row">
                        <div class="col-md-6">
                          <v-select
                            :label="text('profile_label')"
                            v-model="form.stream.h264CodecProfile"
                            :items="h264CodecProfiles">
                            <template v-slot:append-outer>
                              <h264-profile-tooltip/>
                            </template>
                          </v-select>
                        </div>
                        <div class="col-md-6">
                          <v-select
                            :label="text('level_label')"
                            v-model="form.stream.h264CodecLevel"
                            :items="h264CodecLevels">
                            <template v-slot:append-outer>
                              <h26x-level-tooltip/>
                            </template>
                          </v-select>
                        </div>
                      </div>
                    </div>
                    <!-- H.265 Profile and Level -->
                    <div class="col-md-12" v-if="form.video.codec === 'h265'">
                      <div class="row">
                        <div class="col-md-6">
                          <v-select
                            :label="text('profile_label')"
                            v-model="form.stream.h265CodecProfile"
                            :items="h265CodecProfiles">
                            <template v-slot:append-outer>
                              <h265-profile-tooltip/>
                            </template>
                          </v-select>
                        </div>
                        <div class="col-md-6">
                          <v-select
                            :label="text('level_label')"
                            v-model="form.stream.h265CodecLevel"
                            :items="h265CodecLevels">
                            <template v-slot:append-outer>
                              <h26x-level-tooltip/>
                            </template>
                          </v-select>
                        </div>
                      </div>
                    </div>
                    <!-- Resolution -->
                    <div class="col-md-12">
                      <div class="row">
                        <div class="col-md-6">
                          <v-select
                            :label="text('resolution_label')"
                            v-model="form.stream.resolution"
                            @change="onResolutionChange"
                            :items="resolutions">
                          </v-select>
                        </div>
                        <div class="col-md-6" v-if="['Same as source', 'XGA', 'WXGA', 'DCI 2K', '4K UHD', 'DCI 4K'].includes(form.stream.resolution)">
                          <v-text-field
                            :label="text('frame_size_label')"
                            v-model="form.stream.frameSize"
                            disabled>
                          </v-text-field>
                        </div>
                        <div class="col-md-6" v-else-if="form.stream.resolution === 'Custom'">
                          <div class="row">
                            <div class="col-md-5">
                              <v-text-field
                                hide-details
                                :label="text('width_label')"
                                v-model="form.stream.customImageSizeW"
                                :rules="rules.customImageSizeW">
                              </v-text-field>
                            </div>
                            <div class="col-md-2 resolution-delimiter">x</div>
                            <div class="col-md-5">
                              <v-text-field
                                hide-details
                                :label="text('height_label')"
                                v-model="form.stream.customImageSizeH"
                                :rules="rules.customImageSizeH">
                              </v-text-field>
                            </div>
                            <div class="col-md-12 custom-resolution-error p-t-5" v-if="form.stream.resolution === 'Custom' && customResolutionError">
                              <i class="zmdi zmdi-alert-triangle"></i> {{ text(customResolutionError) }}
                            </div>
                          </div>
                        </div>
                        <div class="col-md-6" v-else>
                          <v-select
                            label="Frame size"
                            v-model="form.stream.frameSize"
                            :items="frameSizes[form.stream.resolution]">
                          </v-select>
                        </div>
                      </div>
                    </div>
                    <!-- Aspect Ratio -->
                    <div class="col-md-12">
                      <v-select
                        :label="text('resize_mode_label')"
                        v-model="form.stream.resizeMode"
                        :items="resizeModes">
                        <template v-slot:append-outer>
                          <resize-mode-tooltip/>
                        </template>
                      </v-select>
                    </div>
                    <!-- Bitrate -->
                    <div class="col-md-12">
                      <div class="row">
                        <div class="col-md-6">
                          <v-select
                            :label="text('bitrate_label')"
                            v-model="form.stream.bitrate"
                            :items="videoBitrates"
                            :suffix="videoBitrateSuffix">
                          </v-select>
                        </div>
                        <div class="col-md-6" v-if="form.stream.bitrate === 'Custom'">
                          <v-text-field
                            :label="text('custom_bitrate_label')"
                            v-model="form.stream.customBitrate"
                            suffix="Kbps"
                            :rules="rules.customVideoBitrateRules">
                          </v-text-field>
                        </div>
                      </div>
                    </div>
                    <!-- Frame Rate -->
                    <div class="col-md-12">
                      <div class="row">
                        <div class="col-md-6">
                          <v-select
                            :label="text('frame_rate_label')"
                            v-model="form.stream.frameRate"
                            :items="frameRates"
                            :suffix="frameRateSuffix">
                          </v-select>
                        </div>
                        <div class="col-md-6" v-if="form.stream.frameRate === 'Custom'">
                          <v-text-field
                            :label="text('custom_frame_rate_label')"
                            v-model="form.stream.customFrameRate"
                            suffix="fps"
                            :rules="rules.customFrameRate">
                          </v-text-field>
                        </div>
                      </div>
                    </div>
                    <!-- Reference Frames & B-Frames -->
                    <div class="col-md-12 p-t-20" v-if="['h264', 'h265'].includes(form.video.codec)">
                      <div class="row">
                        <div class="col-md-6">
                          <v-select
                            :label="text('frame_rate')"
                            v-model="form.stream.h264ReferenceFrames"
                            :items="h264ReferenceFrames"
                            suffix="frames">
                            <template v-slot:append-outer>
                              <reference-frames-tooltip/>
                            </template>
                          </v-select>
                        </div>
                        <div class="col-md-6">
                          <v-select
                            label="B-Frames"
                            v-model="form.stream.h264BFrames"
                            :items="h264BFrames"
                            suffix="frames">
                            <template v-slot:append-outer>
                              <b-frames-tooltip/>
                            </template>
                          </v-select>
                        </div>
                      </div>
                    </div>
                    <!-- Keyframe Interval -->
                    <div class="col-md-12">
                      <div class="row">
                        <div class="col-md-6">
                          <v-select
                            label="Keyframe interval"
                            v-model="form.stream.keyframeInt"
                            :items="keyframeInts"
                            :suffix="keyframeIntSuffix"
                            persistent-hint
                            :hint="keyframeIntHint">
                            <template v-slot:append-outer>
                              <keyframe-interval-tooltip/>
                            </template>
                          </v-select>
                        </div>
                        <div class="col-md-6" v-if="form.stream.keyframeInt === 'Custom'">
                          <v-text-field
                            label="Custom keyframe int"
                            v-model="form.stream.customKeyframeInt"
                            suffix="frames"
                            :rules="rules.customKeyframeInt">
                          </v-text-field>
                        </div>
                      </div>
                    </div>
                    <!-- Minimum Keyframe Interval -->
                    <div class="col-md-12" v-if="currentProject === 'file-transcoding'">
                      <div class="row">
                         <div class="col-md-6">
                          <v-select
                            label="Min keyframe interval"
                            v-model="form.stream.minKeyframeInt"
                            :items="keyframeInts"
                            :suffix="minKeyframeIntSuffix"
                            persistent-hint
                            :hint="minKeyframeIntHint">
                            <template v-slot:append-outer>
                              <min-keyframe-interval-tooltip/>
                            </template>
                          </v-select>
                        </div>
                        <div class="col-md-6" v-if="form.stream.minKeyframeInt === 'Custom'">
                          <v-text-field
                            label="Custom min keyframe int"
                            v-model="form.stream.customMinKeyframeInt"
                            suffix="frames"
                            :rules="rules.customMinKeyframeInt">
                          </v-text-field>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn
                    flat
                    @click="streamDialog = false">
                    {{ text('cancel_btn') }}
                  </v-btn>
                  <v-btn
                    flat
                    @click="saveStream('new')"
                    :disabled="isStreamFormValid"
                    color="primary"
                    v-if="!streamEditNumber">
                    {{ text('add_stream_btn') }}
                  </v-btn>
                  <v-btn
                    flat
                    @click="saveEditedStream"
                    :disabled="isStreamFormValid"
                    color="primary"
                    v-if="streamEditNumber">
                    {{ text('save_stream_btn') }}
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <!-- Action button -->
            <div class="row text-right p-t-10">
              <div class="col-md-2 col-l"></div>
              <div class="col-md-10">
                <v-btn
                  large
                  :loading="isSaving"
                  :disabled="isSaving || !ifProfileValid"
                  color="success"
                  @click="saveProfile">
                  <span v-if="!isEditing">{{ text('create_profile_btn') }}</span>
                  <span v-else>{{ text('save_profile_btn') }}</span>
                </v-btn>
              </div>
            </div>
            <!-- Stream Remove Dialog -->
            <v-dialog v-model="streamRemoveDialog" max-width="350">
              <v-card>
                <v-card-title class="headline">
                  {{ text('delete_stream_header') }}
                </v-card-title>
                <v-card-text>
                  <div class="row p-b-20">
                    <div class="col-md-12">
                      {{ text('delete_stream_text') }} #{{ streamRemoveIndex+1 }}?
                    </div>
                  </div>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn depressed small text
                         @click="streamRemoveDialog = false">{{ text('no_btn') }}</v-btn>
                  <v-btn depressed small color="error"
                         @click="deleteStream()">{{ text('yes_btn') }}</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-form>
        </v-card>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import AudioChannelsTooltip from '@/components/transcoding/profile/tooltips/AudioChannelsTooltip.vue';
import ResizeModeTooltip from '@/components/transcoding/profile/tooltips/ResizeModeTooltip.vue';
import KeyframeIntervalTooltip from '@/components/transcoding/profile/tooltips/KeyframeIntervalTooltip.vue';
import MinKeyframeIntervalTooltip from '@/components/transcoding/profile/tooltips/MinKeyframeIntervalTooltip.vue';
import H264ProfileTooltip from '@/components/transcoding/profile/tooltips/H264ProfileTooltip.vue';
import H265ProfileTooltip from '@/components/transcoding/profile/tooltips/H265ProfileTooltip.vue';
import H26xLevelTooltip from '@/components/transcoding/profile/tooltips/H26xLevelTooltip.vue';
import ReferenceFramesTooltip from '@/components/transcoding/profile/tooltips/ReferenceFramesTooltip.vue';
import BFramesTooltip from '@/components/transcoding/profile/tooltips/BFramesTooltip.vue';
import { objectFlip } from '@/helpers/utils';
import {
  AUDIO_CODECS, VIDEO_CODECS, H264_ENCODERS, H265_ENCODERS, AUDIO_BITRATES,
  SAMPLE_RATES, VIDEO_BITRATES, RESOLUTIONS, FRAME_SIZES, RESIZE_MODES, RESIZE_MODE_MAP,
  FRAME_RATES, KEYFRAME_INTS, H264_CODEC_PROFILES, H265_CODEC_PROFILES, H264_CODEC_LEVELS,
  H265_CODEC_LEVELS, H264_REFERENCE_FRAMES, H264_B_FRAMES, VIDEO_CODEC_MAP,
  H264_CODEC_PROFILE_MAP, H265_CODEC_PROFILE_MAP, AUDIO_CHANNELS,
} from '../shared/types';

const defaultStream = {
  resolution: 'Same as source',
  frameSize: 'Same as source',
  customImageSizeW: 1024,
  customImageSizeH: 576,
  bitrate: '500',
  customBitrate: '',
  resizeMode: 'Same as source',
  frameRate: 'Same as source',
  customFrameRate: '',
  keyframeInt: 'Auto',
  customKeyframeInt: '',
  minKeyframeInt: 'Auto',
  customMinKeyframeInt: '',
  h264ReferenceFrames: 1,
  h264BFrames: 2,
  h264CodecProfile: 'H.264 Main Profile',
  h264CodecLevel: '3',
  h265CodecProfile: 'H.265 Main Profile',
  h265CodecLevel: '3',
};

function JsonAPIAudioToNorm(audio) {
  const convertedAudio = {};
  // codec -> codec
  convertedAudio.codec = audio.codec;
  // bitrate -> 2 fields: bitrate, customBitrate
  if (AUDIO_BITRATES.includes(audio.bitrate)) {
    convertedAudio.bitrate = audio.bitrate;
  } else {
    convertedAudio.bitrate = 'Custom';
    convertedAudio.customBitrate = audio.bitrate;
  }
  // channels -> channels
  convertedAudio.channels = Number(audio.channels) || 0;
  // sample_rate -> sampleRate
  const sampleRate = audio.sample_rate;
  convertedAudio.sampleRate = SAMPLE_RATES.includes(sampleRate) ? sampleRate : 'Same as source';
  return convertedAudio;
}

function NormAudioToJsonAPI(audio) {
  const convertedAudio = {};
  // codec -> codec
  convertedAudio.codec = audio.codec;
  // bitrate, customBitrate -> bitrate
  convertedAudio.bitrate = audio.bitrate === 'Custom' ? audio.customBitrate : audio.bitrate;
  // channels -> channels
  convertedAudio.channels = audio.channels;
  // sampleRate -> sample_rate
  convertedAudio.sample_rate = audio.sampleRate === 'Same as source' ? 0 : audio.sampleRate;
  return convertedAudio;
}

function JsonAPIVideoToNorm(video) {
  const convertedVideo = {};
  // codec -> 3 fields: codec, h264encoder, h265encoder
  if (VIDEO_CODEC_MAP.hasOwnProperty(video.codec)) {
    convertedVideo.codec = VIDEO_CODEC_MAP[video.codec][0];
    convertedVideo.h264encoder = VIDEO_CODEC_MAP[video.codec][1] || 'x264';
    convertedVideo.h265encoder = VIDEO_CODEC_MAP[video.codec][2] || 'x265';
  } else {
    convertedVideo.codec = '';
  }
  // streams
  convertedVideo.streams = {
    h264: [],
    h265: [],
    vp8: [],
    xvid: [],
  };
  convertedVideo.streams[convertedVideo.codec] = [];
  return convertedVideo;
}

function NormVideoToJsonAPI(video) {
  const codecMap = {};
  for (const codec in VIDEO_CODEC_MAP) {
    codecMap[VIDEO_CODEC_MAP[codec].join('')] = codec;
  }
  const convertedVideo = {};
  // codec, h264encoder, h265encoder -> codec
  if (video.codec === 'h264') {
    convertedVideo.codec = codecMap[video.codec + video.h264encoder];
  } else if (video.codec === 'h265') {
    convertedVideo.codec = codecMap[video.codec + video.h265encoder];
  } else {
    convertedVideo.codec = codecMap[video.codec];
  }
  // streams
  convertedVideo.streams = [];
  return convertedVideo;
}

function JsonAPIVideoStreamToNorm(stream) {
  const convertedStream = {};
  // resolution -> 4 fields: resolution, frameSize, customImageSizeW, customImageSizeH
  if (stream.resolution) {
    for (let i = 0; i < RESOLUTIONS.length; i++) {
      if (FRAME_SIZES.hasOwnProperty(RESOLUTIONS[i])
        && FRAME_SIZES[RESOLUTIONS[i]].includes(stream.resolution)) {
        convertedStream.resolution = RESOLUTIONS[i];
        convertedStream.frameSize = stream.resolution;
        break;
      }
    }
    if (!convertedStream.resolution) {
      const wh = stream.resolution.split('x');
      convertedStream.resolution = 'Custom';
      convertedStream.customImageSizeW = Number(wh[0]) || '';
      convertedStream.customImageSizeH = Number(wh[1]) || '';
    }
  } else {
    convertedStream.frameSize = 'Same as source';
    convertedStream.resolution = 'Same as source';
  }
  // bitrate -> 2 fields: bitrate, customBitrate
  if (VIDEO_BITRATES.includes(stream.bitrate)) {
    convertedStream.bitrate = stream.bitrate;
  } else {
    convertedStream.bitrate = 'Custom';
    convertedStream.customBitrate = stream.bitrate;
  }
  // aspect_ratio -> 1 field: resizeMode
  convertedStream.resizeMode = RESIZE_MODE_MAP[stream.aspect_ratio] || 'Same as source';
  // target_fps -> 2 fields: frameRate, customFrameRate
  if (stream.target_fps) {
    if (FRAME_RATES.includes(stream.target_fps)) {
      convertedStream.frameRate = stream.target_fps;
    } else {
      convertedStream.frameRate = 'Custom';
      convertedStream.customFrameRate = stream.target_fps;
    }
  } else {
    convertedStream.frameRate = 'Same as source';
  }
  // keyframe_int -> 2 fields: keyframeInt, customKeyframeInt
  const keyInt = Number(stream.keyframe_int) || 'Auto';
  if (KEYFRAME_INTS.includes(keyInt)) {
    convertedStream.keyframeInt = keyInt;
  } else {
    convertedStream.keyframeInt = 'Custom';
    convertedStream.customKeyframeInt = keyInt;
  }
  // min_keyframe_int -> 2 fields: minKeyframeInt, customMinKeyframeInt
  const minKeyInt = Number(stream.min_keyframe_int) || 'Auto';
  if (KEYFRAME_INTS.includes(minKeyInt)) {
    convertedStream.minKeyframeInt = minKeyInt;
  } else {
    convertedStream.minKeyframeInt = 'Custom';
    convertedStream.customMinKeyframeInt = minKeyInt;
  }
  // H26x Settings
  convertedStream.h264ReferenceFrames = stream.h264_reference_frames;
  convertedStream.h264BFrames = stream.h264_bframes;
  convertedStream.h264CodecProfile = H264_CODEC_PROFILE_MAP[stream.h264_codec_profile] || 'H.264 Main Profile';
  convertedStream.h265CodecProfile = H265_CODEC_PROFILE_MAP[stream.h264_codec_profile] || 'H.265 Main Profile';
  convertedStream.h264CodecLevel = H264_CODEC_LEVELS.includes(stream.h264_codec_level) ? stream.h264_codec_level : '3';
  convertedStream.h265CodecLevel = H265_CODEC_LEVELS.includes(stream.h264_codec_level) ? stream.h264_codec_level : '3';
  return convertedStream;
}

function NormVideoStreamToJsonAPI(stream, codec) {
  const resizeModeMap = objectFlip(RESIZE_MODE_MAP);
  const h264CodecProfileMap = objectFlip(H264_CODEC_PROFILE_MAP);
  const h265CodecProfileMap = objectFlip(H265_CODEC_PROFILE_MAP);
  const convertedStream = {};
  // resolution, frameSize, customImageSizeW, customImageSizeH -> resolution
  const w = Number(stream.customImageSizeW) || '';
  const h = Number(stream.customImageSizeH) || '';
  switch (stream.resolution) {
    case 'Same as source':
      convertedStream.resolution = '';
      break;
    case 'Custom':
      convertedStream.resolution = `${w}x${h}`;
      break;
    default:
      convertedStream.resolution = stream.frameSize;
      break;
  }
  // bitrate, customBitrate -> bitrate
  convertedStream.bitrate = stream.bitrate === 'Custom' ? stream.customBitrate : stream.bitrate;
  // resizeMode -> aspect_ratio
  convertedStream.aspect_ratio = stream.resizeMode === 'Same as source' ? '' : resizeModeMap[stream.resizeMode];
  // frameRate, customFrameRate -> target_fps
  switch (stream.frameRate) {
    case 'Same as source':
      convertedStream.target_fps = '';
      break;
    case 'Custom':
      convertedStream.target_fps = stream.customFrameRate;
      break;
    default:
      convertedStream.target_fps = stream.frameRate;
      break;
  }
  // keyframeInt, customKeyframeInt -> keyframe_int
  switch (stream.keyframeInt) {
    case 'Auto':
      convertedStream.keyframe_int = 0;
      break;
    case 'Custom':
      convertedStream.keyframe_int = Number(stream.customKeyframeInt);
      break;
    default:
      convertedStream.keyframe_int = stream.keyframeInt;
      break;
  }
  // minKeyframeInt, customMinKeyframeInt -> min_keyframe_int
  switch (stream.minKeyframeInt) {
    case 'Auto':
      convertedStream.min_keyframe_int = 0;
      break;
    case 'Custom':
      convertedStream.min_keyframe_int = Number(stream.customMinKeyframeInt);
      break;
    default:
      convertedStream.min_keyframe_int = stream.minKeyframeInt;
      break;
  }
  // H26x Settings
  convertedStream.h264_reference_frames = stream.h264ReferenceFrames;
  convertedStream.h264_bframes = stream.h264BFrames;
  switch (codec) {
    case 'h264':
      convertedStream.h264_codec_profile = h264CodecProfileMap[stream.h264CodecProfile];
      convertedStream.h264_codec_level = stream.h264CodecLevel === 'Auto' ? '' : stream.h264CodecLevel;
      break;
    case 'h265':
      convertedStream.h264_codec_profile = h265CodecProfileMap[stream.h265CodecProfile];
      convertedStream.h264_codec_level = stream.h265CodecLevel === 'Auto' ? '' : stream.h265CodecLevel;
      break;
    default:
      break;
  }
  return convertedStream;
}

export default {
  components: {
    AudioChannelsTooltip,
    ResizeModeTooltip,
    KeyframeIntervalTooltip,
    MinKeyframeIntervalTooltip,
    H264ProfileTooltip,
    H265ProfileTooltip,
    H26xLevelTooltip,
    ReferenceFramesTooltip,
    BFramesTooltip,
  },
  created() {
    this.form.stream = Vue.util.extend({}, defaultStream);

    if (this.currentProject === 'live-transcoding') {
      this.routerPrefix = 'Live';
      this.h264CodecLevels = this.h264CodecLevels.filter(v => v !== 'Auto');
      this.h265CodecLevels = this.h265CodecLevels.filter(v => v !== 'Auto');
    }

    if (this.$route.params.id) {
      this.isEditing = true;
      const profileId = this.$route.params.id;
      this.form.id = profileId;
      this.$store.commit('setPageLoader');
      this.$store.dispatch('encodingProfiles/getProfile', profileId)
        .then(() => this.populateForm())
        .catch(() => {
          this.$router.push({ name: `${this.routerPrefix}EncodingProfileListPage` });
        })
        .finally(() => this.$store.commit('resolvePageLoader'));
    }
  },
  data() {
    return {
      isEditing: false,
      isFormValid: false,
      isSaving: false,
      streamEditNumber: 0,
      streamRemoveDialog: false,
      streamRemoveIndex: 0,
      streamDialog: false,
      routerPrefix: '',
      form: {
        id: 0,
        name: '',
        description: '',
        audio: {
          codec: 'mp3',
          bitrate: '128',
          customBitrate: '',
          sampleRate: 'Same as source',
          channels: 0,
        },
        video: {
          codec: 'h264',
          h264encoder: 'x264',
          h265encoder: 'x265',
          streams: {
            h264: [],
            h265: [],
            vp8: [],
            xvid: [],
          },
        },
        stream: {},
      },
      audioCodecs: AUDIO_CODECS,
      videoCodecs: VIDEO_CODECS,
      h264Encoders: H264_ENCODERS,
      h265Encoders: H265_ENCODERS,
      audioChannels: AUDIO_CHANNELS,
      audioBitrates: AUDIO_BITRATES,
      sampleRates: SAMPLE_RATES,
      videoBitrates: VIDEO_BITRATES,
      resolutions: RESOLUTIONS,
      resizeModes: RESIZE_MODES,
      frameSizes: FRAME_SIZES,
      frameRates: FRAME_RATES,
      keyframeInts: KEYFRAME_INTS,
      h264CodecProfiles: H264_CODEC_PROFILES,
      h264CodecLevels: H264_CODEC_LEVELS,
      h265CodecProfiles: H265_CODEC_PROFILES,
      h265CodecLevels: H265_CODEC_LEVELS,
      h264ReferenceFrames: H264_REFERENCE_FRAMES,
      h264BFrames: H264_B_FRAMES,
      rules: {
        nameRules: [
          v => !!v || this.text('title_req_err'),
          v => v.length <= 255 || this.text('profile_max_len_err'),
        ],
        customAudioBitrateRules: [
          v => !!v || this.text('bitrate_required'),
          v => (v > 0 && v <= 1024) || this.text('bitrate_size_err'),
        ],
        customVideoBitrateRules: [
          v => !!v || this.text('bitrate_required'),
          v => (v > 0 && v <= 50000) || this.text('bitrate_video_size_err'),
        ],
        customImageSizeW: [
          v => v >= 160 || v === '' || '',
        ],
        customImageSizeH: [
          v => v >= 120 || v === '' || '',
        ],
        customFrameRate: [
          v => (v > 0 && v <= 120) || this.text('frame_rate_err'),
        ],
        customKeyframeInt: [
          v => (v >= -2 && v <= 1000) || this.text('keyframe_int_err'),
        ],
        customMinKeyframeInt: [
          v => (v >= -2 && v <= 500) || this.text('min_keyframe_int_err'),
        ],
      },
    };
  },
  computed: {
    loading() {
      return this.$store.getters.isDataLoaded;
    },
    currentProject() {
      return this.$store.getters.projectCode;
    },
    editedProfile() {
      return this.$store.getters['encodingProfiles/getProfile'];
    },
    customResolutionError() {
      if (this.form.stream.resolution !== 'Custom') {
        return '';
      }
      if (this.form.stream.customImageSizeW !== '' && this.form.stream.customImageSizeW < 160) {
        return 'width_err';
      }
      if (this.form.stream.customImageSizeH !== '' && this.form.stream.customImageSizeH < 120) {
        return 'height_err';
      }
      if (this.form.stream.customImageSizeW === '' && this.form.stream.customImageSizeH === '') {
        return 'width_or_height';
      }
      return '';
    },
    audioBitrateSuffix() {
      return this.form.audio.bitrate !== 'Custom' ? 'Kbps' : '';
    },
    videoBitrateSuffix() {
      return this.form.stream.bitrate !== 'Custom' ? 'Kbps' : '';
    },
    sampleRateSuffix() {
      return this.form.audio.sampleRate === 'Same as source' ? '' : 'Hz';
    },
    frameRateSuffix() {
      return this.form.stream.frameRate !== 'Custom' ? 'fps' : '';
    },
    keyframeIntSuffix() {
      return (this.form.stream.keyframeInt && this.form.stream.keyframeInt > 1) ? 'frames' : '';
    },
    keyframeIntHint() {
      if (this.form.stream.frameRate === 'Same as source') return '';
      const frameRate = this.form.stream.frameRate > 0 ? this.form.stream.frameRate : this.form.stream.customFrameRate;
      if (frameRate > 0) {
        if (this.form.stream.keyframeInt > 1) {
          const v = (this.form.stream.keyframeInt / frameRate).toFixed(1);
          return `${v} seconds at ${frameRate} fps`;
        }
        if (this.form.stream.customKeyframeInt > 1) {
          const v = (this.form.stream.customKeyframeInt / frameRate).toFixed(1);
          return `${v} seconds at ${frameRate} fps`;
        }
      }
      return '';
    },
    minKeyframeIntSuffix() {
      return (this.form.stream.minKeyframeInt && this.form.stream.minKeyframeInt > 1) ? 'frames' : '';
    },
    minKeyframeIntHint() {
      if (this.form.stream.frameRate === 'Same as source') return '';
      const frameRate = this.form.stream.frameRate > 0 ? this.form.stream.frameRate : this.form.stream.customFrameRate;
      if (frameRate > 0) {
        if (this.form.stream.minKeyframeInt > 0) {
          const v = (this.form.stream.minKeyframeInt / frameRate).toFixed(1);
          return `${v} seconds at ${frameRate} fps`;
        }
        if (this.form.stream.customMinKeyframeInt > 0) {
          const v = (this.form.stream.customMinKeyframeInt / frameRate).toFixed(1);
          return `${v} seconds at ${frameRate} fps`;
        }
      }
      return '';
    },
    isStreamFormValid() {
      if (this.form.stream.resolution === 'Custom' && this.customResolutionError !== '') {
        return true;
      }
      if (this.form.stream.bitrate === 'Custom' && !(this.form.stream.customBitrate > 0 && this.form.stream.customBitrate <= 50000)) {
        return true;
      }
      if (this.form.stream.frameRate === 'Custom' && !(this.form.stream.customFrameRate > 0 && this.form.stream.customFrameRate <= 120)) {
        return true;
      }
      if (this.form.stream.keyframeInt === 'Custom' && !(this.form.stream.customKeyframeInt >= -2 && this.form.stream.customKeyframeInt <= 1000)) {
        return true;
      }
      return this.form.stream.minKeyframeInt === 'Custom' && !(this.form.stream.customMinKeyframeInt >= -2 && this.form.stream.customMinKeyframeInt <= 500);
    },
    ifProfileValid() {
      return this.isFormValid && this.form.video.streams[this.form.video.codec].length;
    },
    dict() {
      return this.$store.getters.dictionary;
    },
  },
  methods: {
    populateForm() {
      if (!this.editedProfile.id) return;
      this.form.id = this.editedProfile.id;
      this.form.name = this.editedProfile.name;
      this.form.audio = JsonAPIAudioToNorm(this.editedProfile.audio_template);
      this.form.video = JsonAPIVideoToNorm(this.editedProfile.video_template);
      const { streams } = this.editedProfile.video_template;
      for (let i = 0; i < streams.length; i++) {
        const convertedStream = JsonAPIVideoStreamToNorm(streams[i]);
        this.form.video.streams[this.form.video.codec].push(this.convertStreamForDisplaying(convertedStream));
      }
    },
    onResolutionChange() {
      switch (this.form.stream.resolution) {
        case 'Same as source':
          this.form.stream.frameSize = 'Same as source';
          break;

        case 'Custom':
          break;

        default:
          this.form.stream.frameSize = this.frameSizes[this.form.stream.resolution][0];
          break;
      }
    },
    saveStream(index) {
      const fromStream = this.form.stream;
      const stream = this.convertStreamForDisplaying(fromStream);

      if (index === 'new') {
        this.form.video.streams[this.form.video.codec].push(stream);
      }
      if (index >= 0) {
        Vue.set(this.form.video.streams[this.form.video.codec], index, stream);
      }
      this.streamDialog = false;
    },
    convertStreamForDisplaying(fromStream) {
      const stream = {
        aspect_ratio: '',
        h264_reference_frames: 0,
        h264_bframes: 0,
      };
      const w = Number(fromStream.customImageSizeW) || '';
      const h = Number(fromStream.customImageSizeH) || '';
      switch (fromStream.resolution) {
        case 'Same as source':
          stream.resolution = 'Same as source';
          break;
        case 'Custom':
          stream.resolution = `${w}x${h}`;
          break;
        default:
          stream.resolution = fromStream.frameSize;
          break;
      }
      stream.bitrate = fromStream.bitrate === 'Custom' ? fromStream.customBitrate : fromStream.bitrate;
      stream.resizeMode = fromStream.resizeMode;
      stream.frameRate = fromStream.frameRate === 'Custom' ? fromStream.customFrameRate : fromStream.frameRate;
      stream.keyframeInt = fromStream.keyframeInt === 'Custom' ? fromStream.customKeyframeInt : fromStream.keyframeInt;
      stream.minKeyframeInt = fromStream.minKeyframeInt === 'Custom' ? fromStream.customMinKeyframeInt : fromStream.minKeyframeInt;
      stream.h264CodecProfile = fromStream.h264CodecProfile;
      stream.h264CodecLevel = fromStream.h264CodecLevel;
      stream.h265CodecProfile = fromStream.h265CodecProfile;
      stream.h265CodecLevel = fromStream.h265CodecLevel;
      stream.h264ReferenceFrames = fromStream.h264ReferenceFrames;
      stream.h264BFrames = fromStream.h264BFrames;
      stream.edit = Vue.util.extend({}, fromStream);
      return stream;
    },
    editStream(i, stream) {
      this.streamEditNumber = i + 1;
      this.form.stream = Vue.util.extend({}, stream.edit);
      this.streamDialog = true;
    },
    saveEditedStream() {
      this.saveStream(this.streamEditNumber - 1);
    },
    addNewStream() {
      this.streamEditNumber = 0;
      this.streamDialog = true;
      this.form.stream = Vue.util.extend({}, defaultStream);
    },
    openStreamRemoveDialog(i) {
      this.streamRemoveDialog = true;
      this.streamRemoveIndex = i;
    },
    deleteStream() {
      this.form.video.streams[this.form.video.codec].splice(this.streamRemoveIndex, 1);
      this.streamRemoveDialog = false;
    },
    saveProfile() {
      this.isSaving = true;

      const profile = {
        id: this.form.id,
        name: this.form.name,
        audio_template: NormAudioToJsonAPI(this.form.audio),
        video_template: NormVideoToJsonAPI(this.form.video),
      };
      const streams = this.form.video.streams[this.form.video.codec];
      for (let i = 0; i < streams.length; i++) {
        const stream = streams[i].edit;
        const convertedStream = NormVideoStreamToJsonAPI(stream, this.form.video.codec);
        profile.video_template.streams.push(convertedStream);
      }

      if (this.isEditing) {
        this.$store.dispatch('encodingProfiles/updateProfile', profile)
          .then(() => {
            this.$router.push({
              name: `${this.routerPrefix}EncodingProfileDetailPage`,
              params: {
                id: profile.id,
              },
            });
          })
          .catch((errorMsg) => {
            this.isSaving = false;
            this.$toasted.error(errorMsg, {
              theme: 'toasted-primary',
              position: 'top-center',
              duration: 7000,
            });
          });
      } else {
        this.$store.dispatch('encodingProfiles/createProfile', profile)
          .then((result) => {
            this.$router.push({
              name: `${this.routerPrefix}EncodingProfileDetailPage`,
              params: {
                id: result.id,
              },
            });
          })
          .catch((errorMsg) => {
            this.isSaving = false;
            this.$toasted.error(errorMsg, {
              theme: 'toasted-primary',
              position: 'top-center',
              duration: 7000,
            });
          });
      }
    },
    text(code) {
      // eslint-disable-next-line no-prototype-builtins
      if (this.dict.hasOwnProperty(code)) {
        return this.dict[code];
      }
      return code;
    },
  },
};
</script>
